<template>
	<v-list-item class="promo-product-item px-3 py-2" :class="status ? `promo-product-item--${status}` : ''" @click="emit('open')">
		<div class="d-flex align-center ga-3">
			<div class="flex-grow-1">
				<v-chip v-if="free" color="success" variant="flat" size="small">{{ product?.label }}</v-chip>
				<ProductLabel v-else :params="{ value: product?.label || '', subtitle: product?.ean, imageThumbnailUid: product?.imageThumbnailUid, avatarSize: 40 }" />
				<div class="d-flex align-center flex-wrap ga-2 mt-1 text-body-small text-medium-emphasis">
					<template v-if="nego">
						<span>{{ negoSummary }}</span>
						<v-chip v-if="additionalCount" size="x-small" color="warning" prepend-icon="add_location_alt">{{ additionalCount }}</v-chip>
					</template>
					<template v-else>
						<v-icon v-if="present" size="small" :color="present === PRESENCE.PRESENT ? 'success' : 'error'">{{ present === PRESENCE.PRESENT ? 'visibility' : 'visibility_off' }}</v-icon>
						<span>{{ format.uc(target.statedUC) }} / {{ format.uc(target.targetUC) }}</span>
						<v-chip v-if="present === PRESENCE.PRESENT && target.targetUC" size="x-small" :color="target.reached ? 'success' : 'warning'" label>{{ target.reached ? '✓' : `${format.number(target.gap)} ${t('UC')}` }}</v-chip>
					</template>
				</div>
			</div>
			<v-icon color="primary">chevron_right</v-icon>
		</div>
	</v-list-item>
</template>

<script setup>
import { computed } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { NEGO, getProduct } from '@/utils/domain/promo/promoModel';
import { isNegoPhase } from '@/utils/domain/promo/promoPhase';
import { negotiatedRowOf } from '@/utils/domain/promo/promoNego';
import { freeRowOf } from '@/utils/domain/promo/promoFree';
import { additionalOf } from '@/utils/domain/promo/promoAdditional';
import { PRESENCE, productTarget, statedRowOf } from '@/utils/domain/promo/promoStatement';
import { productStatus } from '@/utils/domain/promo/promoStatus';
import { sumOf } from '@/utils/domain/promo/promoPrimitives';
import ProductLabel from '@/components/common/ProductLabel.vue';

const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	phase: { type: String, required: true },
	productId: { type: Number, required: true },
	free: { type: Boolean, default: false },
});
const emit = defineEmits(['open']);

const { snapshot, format, t } = usePromoStep();

const nego = computed(() => isNegoPhase(props.phase));
const product = computed(() => getProduct(snapshot.value, props.productId));
const status = computed(() => productStatus(snapshot.value, props.campaign, props.offer.id, props.productId, props.offerRef.offerKey, props.free));
const row = computed(() => (props.free ? freeRowOf(snapshot.value, props.offerRef, props.productId) : negotiatedRowOf(snapshot.value, props.offerRef, props.productId)));
const additional = computed(() => (props.free ? [] : additionalOf(snapshot.value, props.offerRef, props.productId, NEGO)));
const additionalCount = computed(() => additional.value.length);
const present = computed(() => statedRowOf(snapshot.value, props.offerRef, props.productId, props.free)?.present || PRESENCE.NONE);
const target = computed(() => productTarget(snapshot.value, props.offerRef, props.productId, props.free));

const containerLabel = (id) => snapshot.value.refs.containers.find((c) => c.id === id)?.label || '';

const negoSummary = computed(() => {
	const volume = Number(row.value?.volume) || 0;
	if (!volume) return '—';
	const totalUC = (Number(row.value?.volumeUC) || 0) + (sumOf(additional.value, 'volumeUC') || 0);
	return `${format.number(volume)} ${containerLabel(row.value.productContainerId)} · ${format.uc(totalUC)}`;
});
</script>
